import React from "react";

import { InfoMessage } from "@amphore-dev/amphore-lib";
import { t } from "i18next";

import { useQuery } from "@tanstack/react-query";

import WorkspaceService from "@/services/WorkspaceService";

import { useAuth } from "@/hooks/useAuth";
import { useSubscription } from "@/hooks/useSubscription";
import { useWorkspaceParam } from "@/hooks/useWorkspaceParam";

import { WorkspacePicker } from "@/components/organisms";
import { MembersCard } from "@/components/organisms/MembersCard/MembersCard";
import { PageLayout } from "@/components/templates";

import { workspaceKeys } from "@/constants/CQueryKeys";

import { WorkspaceIdentityCard } from "./WorkspaceIdentityCard";

/**
 * /workspace: the workspace the app works in - its identity (logo, name,
 * description), then everyone in it and what their role grants across it.
 * A role here is the floor — every client and every project of the
 * workspace — and the clients or projects opened to someone on their
 * own unfold under them as exceptions (docs/modele.md, phase 5).
 *
 * The workspace's id comes with `GET /subscription`, which is fetched
 * for the plan anyway; `canManage` is its owner/admin answer. A NONE
 * member (given a project, not the workspace) gets no identity card:
 * the API answers them 404, and the switcher already names it.
 */
export const WorkspaceMembers: React.FC = () => {
	const { user } = useAuth();
	// Any workspace the user is a member of, NONE aside: someone given a
	// project only has no page about the workspace (the API answers 404).
	const picked = useWorkspaceParam(["OWNER", "ADMIN", "EDITOR", "VIEWER"]);
	const { state, isLoading, error } = useSubscription(picked.workspaceId);
	const workspaceId = state?.workspaceId;
	const isMember = !!user && picked.current?.role !== "NONE";

	const identity = useQuery({
		queryKey: workspaceKeys.detail(workspaceId ?? 0),
		queryFn: () => WorkspaceService.get(workspaceId!),
		enabled: !!workspaceId && isMember,
	});

	return (
		<PageLayout
			title={t("workspace.title")}
			isLoading={isLoading}
			error={!!error}
		>
			<div className="flex flex-col gap-8">
				<WorkspacePicker
					options={picked.options}
					value={picked.current?.id}
					onChange={picked.select}
				/>
				{!!identity.data && (
					<WorkspaceIdentityCard workspace={identity.data} />
				)}
				<section className="flex max-w-3xl flex-col gap-4">
					<InfoMessage color="info">
						{t("workspace.members.help")}
					</InfoMessage>
					{!!state && !!user && (
						<MembersCard
							scope="workspaces"
							id={state.workspaceId}
							canManage={state.canManage}
							currentUserId={user.id}
						/>
					)}
					<p className="text-sm text-neutral-500">
						{t("workspace.members.none")}
					</p>
				</section>
			</div>
		</PageLayout>
	);
};
